import axios from 'axios'
import store from '@/store'
import router from '@/router'
import i18n from '@/i18n'
import { logout } from '@/utils/user'

import { showNotice } from '@/utils/tool'

import { setupCache } from 'axios-cache-adapter'
import localforage from 'localforage'
const forageStore = localforage.createInstance({
  // List of drivers used
  driver: [
    localforage.LOCALSTORAGE
  ],
  // Prefix all storage keys to prevent conflicts
  name: 'my-cache'
})

// Create `axios-cache-adapter` instance
const cache = setupCache({
  store: forageStore
})

// ETag 快取：第一次收到資料時把 ETag 與資料存進 IndexedDB（localStorage 只有 5MB 不夠放），
// 之後帶 If-None-Match，後端回 304 就改用 IndexedDB 的資料
// 呼叫端以 customConfig: { etagCache: true } 開啟
const etagStore = localforage.createInstance({
  driver: [
    localforage.INDEXEDDB
  ],
  name: 'etag-cache'
})

function getEtagCacheKey(config, formData) {
  const params = []
  for (const [key, value] of formData.entries()) {
    if (key !== 'token') params.push(`${key}=${value}`)
  }
  params.sort()
  return (config.baseURL || '') + config.url + '?' + params.join('&')
}

async function readEtagCache(key) {
  try {
    const cached = await etagStore.getItem(key)
    return cached && cached.etag ? cached : null
  } catch (e) {
    // 不支援 IndexedDB（如部分無痕模式）就當作沒有快取
    return null
  }
}

function writeEtagCache(key, etag, data) {
  etagStore.setItem(key, { etag, data }).catch(() => {
    // 寫入失敗：清掉這筆，下次就不會帶 If-None-Match
    etagStore.removeItem(key).catch(() => {})
  })
}

// create an axios instance
const service = axios.create({
  adapter: cache.adapter,
  timeout: 60000 // request timeout
})
// request interceptor
service.interceptors.request.use(
  async config => {
    if (!(config.customConfig && config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_PLUS')
    }

    const formData = new FormData()
    for (var key in config['data']) {
      if (config['data'][key] !== null && config['data'][key] !== undefined) {
        formData.set(key, config['data'][key])
      }
    }

    if (!formData.has('code')) {
      let brandCode = store.getters.brandCode
      if (!brandCode || brandCode === '') {
        brandCode = process.env.VUE_APP_BRAND_CODE // 開發環境指定brand code
        if (brandCode === '') {
          brandCode = window.location.host || document.location.host // 剛開始載入先用host取資訊
        }
      }

      formData.set('code', brandCode)
    }

    if (!formData.has('token') && store.getters.token) {
      formData.set('token', store.getters.token)
    }

    if (!formData.has('language')) {
      formData.set('language', store.getters.language)
    }

    config['data'] = formData

    if (config.customConfig && config.customConfig.etagCache) {
      const cacheKey = getEtagCacheKey(config, formData)
      const cached = await readEtagCache(cacheKey)
      config.etagCacheKey = cacheKey
      if (cached) {
        config.headers['If-None-Match'] = cached.etag
      }
      config.validateStatus = status => (status >= 200 && status < 300) || status === 304
    }

    // store.commit('SET_LOADING', true)
    return config
  },
)

// response interceptor
service.interceptors.response.use(
  async function(response) {
    if (!(response.config.customConfig && response.config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_MINUS')
    }

    const cacheKey = response.config.etagCacheKey
    if (cacheKey) {
      if (response.status === 304) {
        // 版本沒變，用本地資料
        const cached = await readEtagCache(cacheKey)
        response.data = cached ? cached.data : response.data
      } else {
        const etag = response.headers['etag']
        if (etag) writeEtagCache(cacheKey, etag, response.data)
      }
    }

    return response
  },
  error => {
    if (!(error.config.customConfig && error.config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_MINUS')
    }
    store.commit('SET_LOADING', false)
    const showErrorMsg = !(error.config && error.config.customConfig && error.config.customConfig.mute)

    if (error.response === undefined) {
      // 其他錯誤訊息
      const msg = i18n.te('Bao.please_check_network') ? i18n.t('Bao.please_check_network') : 'Please check your network status.'
      if (showErrorMsg) {
        showNotice(msg, function() {
          if (router.currentRoute.name === 'error' || router.currentRoute.name === 'critical') {
            window.top.location.href = '/'
          } else {
            window.top.location.reload()
          }
        })
      }
    } else {
      const content = error.response.data
      // console.clear()
      if (content.status === 401) {
        // 重新登入
        logout(content.msg)
      } else if (content.status === 403) {
        // 權限不足 回首頁
        // showNotice(content.msg, function() {
        //   router.push({ path: '/index' })
        // })
      } else if (content.status === 510) {
        // toErrorPage(content.data.content)
      } else if (content.status === 406) {
        // 表單欄位顯示 不由這邊處理
      } else if (content.status === 505) {
        // 不由這邊處理
      } else {
        // 其他錯誤訊息
        if (showErrorMsg) {
          showNotice(content.msg)
        }
      }
    }

    return Promise.reject(error)
  }
)

export default service
